export interface AgentStatusChangedPayload {
  endpointId: string;
  tenantId: string;
  status: string;
  customLabel: string | null;
  updatedAt: string | null;
  /** Monotonically increasing counter; undefined on rows written before the SIP-resilience schema migration. */
  statusVersion?: number;
  /** Who initiated the change. "system_conversations_tab" means the auto-status feature set it;
   *  anything else (e.g. "agent_manual", "supervisor_manual") means the agent changed it themselves. */
  changeSource?: string | null;
}

export interface DashboardStatsChangedPayload {
  tenantId: string;
  callLogId: string;
  status: string;
  startedAt: string | Date | null;
  endedAt: string | Date | null;
}

/** Emitted after a call rejection write succeeds so the dashboard refreshes
 *  per-agent missed/rejected counts. This is NOT a call lifecycle event:
 *  clients must only invalidate /api/dashboard-stats in response to it,
 *  never add/remove live calls or infer call termination from it. */
export interface DashboardAgentAttemptChangedPayload {
  tenantId: string;
  agentEndpointId: string;
}

export interface QueueNoAgentWaitingPayload {
  callUuid: string;
  queueId: string;
  queueName: string;
  tenantId: string;
  timestamp: string;
}

export interface TicketUpdatedPayload {
  tenantId: string;
  ticketId: string;
  action: "created" | "updated" | "replied";
  status?: string;
  priority?: string;
  assigneeUserId?: string | null;
  unreadCount?: number;
  lastMessageAt?: string;
}

export interface NewTicketPayload {
  ticketId: string;
  subject: string;
  fromEmail?: string | null;
  toEmail?: string | null;
}

export interface NewVoicemailPayload {
  callLogId?: string | null;
  callerNumber?: string | null;
  callerName?: string | null;
  fromNumber?: string | null;
  toNumber?: string | null;
  queueName?: string | null;
  duration?: number | null;
}

export interface VoicemailReadStateChangedPayload {
  id: string;
  vmIsNew: boolean;
  vmArchived?: boolean;
  vmStatus?: string | null;
  vmNotes?: string | null;
  vmHandledBy?: string | null;
}

export interface VoicemailLifecycleChangedPayload {
  reason?: "status-archived" | "status-unarchived" | "status-deleted";
}

export interface VoicemailStatusChangedPayload {
  id?: string | null;
  reason: "created" | "updated" | "deleted" | "reordered";
}

export interface GenericNotificationPayload {
  type?: string;
  title: string;
  message?: string | null;
  body?: string | null;
  conversationId?: string | null;
}

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  preview: string;
  count: number;
  timestamp: string;
  entityId: string | null;
  link: string;
  channelBreakdown?: Record<string, number>;
}

export interface NotificationCreatedPayload {
  tenantId: string;
  notification?: NotificationItem;
}

export interface CampaignUpdatedPayload {
  tenantId: string;
  campaignId: string;
  status: string;
  name?: string;
}

export interface TenantSettingsChangedPayload {
  tenantId: string;
  statusAfterLogin: string | null;
}

export interface NewMessagePayload {
  id: string;
  conversationId: string;
  tenantId: string;
  direction: string;
  channel: string;
  body: string | null;
  fromAddress?: string | null;
  toAddress?: string | null;
  contactName?: string | null;
  widgetName?: string | null;
  sessionId?: string | null;
  toNumberFriendlyName?: string | null;
  externalMessageId?: string | null;
  deliveryStatus?: string | null;
  sentAt?: string | null;
  senderUserId?: string | null;
  isInternal?: boolean;
  attachments?: Array<{ name: string; size: number; contentType: string; objectPath: string }>;
  /**
   * The conversation's current assignee at the time the inbound event is
   * published. A null value means the conversation is in the shared Inbox.
   */
  assigneeUserId?: string | null;
  /** True when the conversation is currently assigned to an AI bot user.
   *  Clients use this to suppress sound alerts and browser push notifications
   *  for messages the bot is handling. False (or absent) means notifications
   *  should fire normally. */
  handledByBot?: boolean;
}

/**
 * Decide whether this client should receive an alert for an inbound customer
 * message. An open conversation is already visible and must not produce an
 * additional browser notification or sound.
 */
export function shouldNotifyForConversationMessage(
  payload: NewMessagePayload,
  currentUserId: string | null | undefined,
  isActivelyViewingConversation: boolean,
): boolean {
  if (payload.direction !== "inbound" || payload.handledByBot === true) return false;
  if (isActivelyViewingConversation) return false;

  // Shared-Inbox conversations remain visible to every human agent until one
  // claims them, except in the tab where the conversation is already open.
  if (!payload.assigneeUserId) return true;

  // A human-assigned conversation alerts only its assignee.
  return payload.assigneeUserId === currentUserId;
}

/** Decide whether this client should play the Conversation sound. */
export function shouldPlayConversationMessageSound(
  payload: NewMessagePayload,
  currentUserId: string | null | undefined,
  isActivelyViewingConversation: boolean,
): boolean {
  return shouldNotifyForConversationMessage(
    payload,
    currentUserId,
    isActivelyViewingConversation,
  );
}

export interface MessageStatusUpdatedPayload {
  messageId: string;
  conversationId: string;
  deliveryStatus: string;
  deliveryError?: string | null;
}

export interface ConversationUpdatedPayload {
  conversationId: string;
  status?: string;
  assigneeUserId?: string | null;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  unreadCount?: number;
  disposition?: string | null;
  dispositionNote?: string | null;
}

/**
 * Published only when a Conversation changes from assigned to unassigned.
 * It deliberately carries the identifying details the notification surfaces
 * need, rather than treating the transition as a customer message.
 */
export interface ConversationUnassignedPayload {
  eventId: string;
  conversationId: string;
  tenantId: string;
  channel: string;
  sourceAddress?: string | null;
  inboxAddress?: string | null;
  contactName?: string | null;
  widgetName?: string | null;
  occurredAt: string;
  /** The previous assignee was the tenant AI Bot. */
  wasAssignedToAiBot: boolean;
  /**
   * An explicit handoff requested by an agent should alert the human team even
   * when the previous assignee was the AI Bot.
   */
  alertAgents?: boolean;
}

export function shouldNotifyForConversationUnassignment(
  payload: ConversationUnassignedPayload,
): boolean {
  return payload.alertAgents === true || payload.wasAssignedToAiBot !== true;
}

/**
 * The notification bell is a shared work-queue view. It must refresh for every
 * assignment-to-Inbox transition, including a silent AI Bot-to-human handoff.
 */
export function shouldRefreshNotificationSummaryForConversationUnassignment(
  payload: ConversationUnassignedPayload,
): boolean {
  return Boolean(payload.conversationId);
}

export interface NewLivechatSessionPayload {
  conversationId: string;
  sessionId: string;
}

export interface TeamChatMessagePayload {
  id: string;
  channelId: string;
  senderUserId: string;
  senderDisplayName: string;
  body: string;
  createdAt: string;
  isDeleted?: boolean;
  attachments?: Array<{ id: string; objectPath: string; contentType: string; filename: string; size: number }>;
}

export interface TeamChatTypingPayload {
  channelId: string;
  userId: string;
  displayName: string;
}

export interface TeamChatChannelEventPayload {
  channelId: string;
}

export interface SupportAttachment {
  id?: string;
  objectPath: string;
  contentType: string;
  filename: string;
  size: number;
}

export interface SupportMessagePayload {
  id: string;
  sessionId: string;
  senderId: string;
  senderRole: string;
  senderName?: string | null;
  body: string;
  isInternal?: boolean | null;
  attachments?: SupportAttachment[];
  createdAt: string;
}

export interface SupportNewSessionPayload {
  id: string;
  userId: string;
  status: string;
  priority?: string | null;
  subject?: string | null;
  createdAt?: string;
}

export interface SupportSessionUpdatedPayload {
  sessionId: string;
  message?: SupportMessagePayload;
  status?: string;
}

export interface SupportSessionClosedPayload {
  sessionId: string;
}

export interface SupportSessionReopenedPayload {
  sessionId: string;
}

export interface SmsCampaignUpdatedPayload {
  campaignId: string;
  tenantId: string;
  status?: string;
  totalContacts?: number;
  sentCount?: number;
  deliveredCount?: number;
  undeliveredCount?: number;
  failedCount?: number;
  processedCount?: number;
  blockedCount?: number;
  preparationError?: string | null;
}

export interface SmsCampaignMessageStatusPayload {
  campaignId: string;
  messageId: string;
  status: string;
  updatedAt: string;
}

export interface LivechatMessagePayload {
  id: string;
  conversationId?: string | null;
  sessionId?: string | null;
  body: string | null;
  direction: string;
  channel?: string | null;
  fromAddress?: string | null;
  toAddress?: string | null;
  sentAt?: string | null;
  senderUserId?: string | null;
  /** Resolved agent display name — nickname when nicknamesEnabled, otherwise displayName. */
  senderName?: string | null;
  attachments?: SupportAttachment[];
}

/** Sent directly to a visitor's Live Chat session when the conversation is ended. */
export interface LivechatSessionEndedPayload {
  sessionId: string;
  status: "ended";
  endedAt: string;
}

export interface AgentTypingPayload {
  isTyping: boolean;
  agentName?: string | null;
}

export interface TransferCancelledPayload {
  transferId: string;
  agentCallUuid: string;
  customerReconnected: boolean;
}

export interface TransferCancelReconnectFailedPayload {
  transferId: string;
  agentCallUuid: string;
  message: string;
}

export interface PlatformStatsChangedPayload {
  reason: "tenant_created" | "tenant_deleted" | "balance_changed" | "call_billed";
}

export interface SipReregisterPayload {
  endpointId: string;
  tenantId: string;
  /** Current health episode ID; browser SDK uses this to guard stale recovery attempts. */
  healthEpisodeId?: string;
}

export interface RecordingReadyPayload {
  callLogId: string;
  tenantId: string;
  /** S3 key of the combined stitched recording. Omitted for single-segment calls. */
  s3Key?: string;
}

export interface RecordingFailedPayload {
  callLogId: string;
  tenantId: string;
  permanentlyFailed: boolean;
}

export type RecordingStatusChangedPayload = import("./recording-contracts").RecordingStatusChangedPayload;

export interface SoundNotificationSettingsChangedPayload {
  tenantId: string;
  /** Monotonically increasing version (e.g. Date.now()) stored alongside the
   *  settings. Clients ignore events with a version ≤ their last applied version. */
  settingsVersion: number;
  conversationEnabled: boolean;
  conversationVolume: number;
  conversationAudioUrl: string | null;
  queueEnabled: boolean;
  queueVolume: number;
  queueSoundRoles: string[];
  queueIds: string[];
  queueAudioUrl: string | null;
}

export interface LiveChatWidgetAvailabilityPayload {
  /** The public widget key. This payload intentionally contains no tenant data. */
  widgetId: string;
  active: boolean;
  version: number;
  updatedAt: string;
}

export type PusherEventMap = {
  "platform-stats-changed": PlatformStatsChangedPayload;
  "agent-status-changed": AgentStatusChangedPayload;
  "sip-reregister": SipReregisterPayload;
  "dashboard-stats-changed": DashboardStatsChangedPayload;
  "dashboard-agent-attempt-changed": DashboardAgentAttemptChangedPayload;
  "queue-no-agent-waiting": QueueNoAgentWaitingPayload;
  "ticket-updated": TicketUpdatedPayload;
  "new-ticket": NewTicketPayload;
  "new-voicemail": NewVoicemailPayload;
  "voicemail-read-state-changed": VoicemailReadStateChangedPayload;
  "voicemail-lifecycle-changed": VoicemailLifecycleChangedPayload;
  "voicemail-status-changed": VoicemailStatusChangedPayload;
  "notification": GenericNotificationPayload;
  "notification-created": NotificationCreatedPayload;
  "campaign-updated": CampaignUpdatedPayload;
  "tenant-settings-changed": TenantSettingsChangedPayload;
  "new-message": NewMessagePayload;
  "message-status-updated": MessageStatusUpdatedPayload;
  "conversation-updated": ConversationUpdatedPayload;
  "conversation-unassigned": ConversationUnassignedPayload;
  "new-livechat-session": NewLivechatSessionPayload;
  "team-chat-message": TeamChatMessagePayload;
  "team-chat-typing": TeamChatTypingPayload;
  "team-chat-channel-created": TeamChatChannelEventPayload;
  "team-chat-channel-deleted": TeamChatChannelEventPayload;
  "support-new-session": SupportNewSessionPayload;
  "support-session-updated": SupportSessionUpdatedPayload;
  "support-message": SupportMessagePayload;
  "support-session-closed": SupportSessionClosedPayload;
  "support-session-reopened": SupportSessionReopenedPayload;
  "sms-campaign-updated": SmsCampaignUpdatedPayload;
  "sms-campaign-message-status": SmsCampaignMessageStatusPayload;
  "livechat-message": LivechatMessagePayload;
  "livechat-session-ended": LivechatSessionEndedPayload;
  "livechat-ip-blocked": Record<string, never>;
  "agent-typing": AgentTypingPayload;
  "transfer-cancelled": TransferCancelledPayload;
  "transfer-cancel-reconnect-failed": TransferCancelReconnectFailedPayload;
  "recording-ready": RecordingReadyPayload;
  "recording-failed": RecordingFailedPayload;
  "recording-status-changed": RecordingStatusChangedPayload;
  "sound-notification-settings-changed": SoundNotificationSettingsChangedPayload;
  "livechat-widget-availability": LiveChatWidgetAvailabilityPayload;
};
